import type { CSSProperties } from 'react';
import type { SxProps, Theme } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';

/**
 * Editorial tokens — outer surface lighter than fields (“sunken” inputs).
 * Modal / page: ~#1A1A1A; inputs & shells: ~#121212 (Create Resume reference).
 */
export const EDITORIAL = {
    background: '#1a1a1a',
    surfaceLow: '#121212',
    surfaceContainer: '#161616',
    surfaceContainerHigh: '#1f1f1f',
    surfaceVariant: '#2a2a2a',
    onSurface: '#ffffff',
    onSurfaceVariant: '#adaaaa',
    outline: '#767575',
    outlineVariant: '#484847',
    primary: '#ffc965',
    primaryContainer: '#feb700',
    onPrimaryFixed: '#392700',
    focusRing: '0 0 0 1px rgba(255, 201, 101, 0.4)',
    captionMuted: '#6c6d6e',
} as const;

export const editorialInputShellSx: SxProps<Theme> = {
    bgcolor: EDITORIAL.surfaceLow,
    borderRadius: '12px',
    p: '4px',
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
    '&:focus-within': {
        bgcolor: EDITORIAL.surfaceContainer,
        boxShadow: EDITORIAL.focusRing,
    },
};

/** TextField / outlined inputs: transparent inside shell, no outline */
export const editorialOutlinedInputSx: SxProps<Theme> = {
    mt: 0,
    mb: 0,
    bgcolor: 'transparent',
    '& .MuiOutlinedInput-root': {
        bgcolor: 'transparent',
        borderRadius: '10px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
    '& .MuiInputBase-input': {
        color: EDITORIAL.onSurface,
        py: 2,
        px: 2,
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        fontSize: 14,
    },
    '& .MuiInputBase-input::placeholder': {
        color: EDITORIAL.outline,
        opacity: 1,
    },
};

export const editorialDropdownBentoSx: SxProps<Theme> = {
    bgcolor: EDITORIAL.surfaceLow,
    borderRadius: '12px',
    p: 3,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
};

export const editorialSelectSx: SxProps<Theme> = {
    mt: 0,
    color: EDITORIAL.onSurface,
    bgcolor: 'transparent',
    '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none',
        boxShadow: 'none',
    },
    '& .MuiSelect-select': {
        bgcolor: EDITORIAL.surfaceContainer,
        borderRadius: '8px',
        py: 1.5,
        px: 2,
        display: 'flex',
        alignItems: 'center',
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
    },
    '& .MuiSvgIcon-root': {
        color: EDITORIAL.outline,
        right: 12,
    },
    '&.Mui-focused': {
        boxShadow: EDITORIAL.focusRing,
        borderRadius: '10px',
    },
};

export const editorialMenuPaperSx: SxProps<Theme> = {
    bgcolor: EDITORIAL.surfaceContainer,
    color: EDITORIAL.onSurface,
    border: `1px solid ${EDITORIAL.outlineVariant}`,
    borderRadius: '8px',
};

export const editorialFormHeaderTypographySx: SxProps<Theme> = {
    color: EDITORIAL.onSurfaceVariant,
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    mb: 1.5,
    fontStyle: 'normal',
};

export const editorialModalContainerSx = (isMobile: boolean): SxProps<Theme> => ({
    bgcolor: EDITORIAL.background,
    boxShadow: isMobile ? 'none' : '0 25px 50px rgba(0,0,0,0.5)',
    borderRadius: isMobile ? 0 : '12px',
    border: isMobile ? 'none' : `1px solid ${EDITORIAL.outlineVariant}`,
    p: { xs: 3, sm: 4 },
});

export const editorialModalTitleSx: SxProps<Theme> = {
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontSize: { xs: '1rem', sm: '1.125rem' },
    fontWeight: 800,
    color: EDITORIAL.onSurface,
    mb: 2.5,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
};

export const editorialStickyFooterSx: SxProps<Theme> = {
    position: 'sticky',
    bottom: 0,
    zIndex: 2,
    py: 2,
    mt: 2,
    background: 'rgba(26, 26, 26, 0.78)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    boxShadow: '0 -20px 40px rgba(0,0,0,0.4)',
    borderRadius: '12px 12px 0 0',
};

export const editorialSubmitButtonSx: SxProps<Theme> = {
    border: 'none',
    background: `linear-gradient(to right, ${EDITORIAL.primary}, ${EDITORIAL.primaryContainer})`,
    borderRadius: '9999px',
    minHeight: 52,
    boxShadow: '0 4px 20px rgba(255,201,101,0.2)',
    '&:hover': {
        border: 'none',
        background: `linear-gradient(to right, ${EDITORIAL.primaryContainer}, #ecaa00)`,
        color: EDITORIAL.onPrimaryFixed,
        WebkitTextFillColor: EDITORIAL.onPrimaryFixed,
    },
    '&.Mui-disabled': {
        border: 'none',
        background: EDITORIAL.outlineVariant,
        color: EDITORIAL.onSurfaceVariant,
        WebkitTextFillColor: EDITORIAL.onSurfaceVariant,
    },
};

export const editorialSubmitTextStyle: CSSProperties = {
    color: EDITORIAL.onPrimaryFixed,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    fontSize: 15,
};

export const editorialSliderSx: SxProps<Theme> = {
    color: EDITORIAL.primary,
    height: 8,
    '& .MuiSlider-thumb': {
        width: 16,
        height: 16,
        backgroundColor: EDITORIAL.primary,
        border: `4px solid ${EDITORIAL.background}`,
        boxShadow: '0 0 10px rgba(255,201,101,0.35)',
        '&:hover, &.Mui-focusVisible': {
            boxShadow: '0 0 0 8px rgba(255,201,101,0.16)',
        },
    },
    '& .MuiSlider-track': {
        background: EDITORIAL.primary,
        border: 'none',
        boxShadow: '0 0 10px rgba(255,201,101,0.25)',
    },
    '& .MuiSlider-rail': {
        bgcolor: EDITORIAL.surfaceVariant,
        opacity: 1,
    },
};

export const editorialRangeSectionSx: SxProps<Theme> = {
    bgcolor: EDITORIAL.surfaceLow,
    borderRadius: '12px',
    p: 3,
    mt: 1,
};

export const editorialCloseIconButtonSx: SxProps<Theme> = {
    color: EDITORIAL.primary,
    '&:hover': {
        bgcolor: EDITORIAL.surfaceContainer,
    },
};

export const editorialCharCountSx: SxProps<Theme> = {
    display: 'block',
    textAlign: 'right',
    mt: 0.5,
    fontFamily: FONT_ENUM.MAIN_MEDIUM,
    fontSize: 10,
    color: EDITORIAL.captionMuted,
};

export const editorialScrollRootSx: SxProps<Theme> = {
    bgcolor: EDITORIAL.background,
    color: EDITORIAL.onSurface,
};
